import React, { useState, useEffect } from 'react';
import ImageUpload from './ImageUpload';
import ResponseDisplay from './ResponseDisplay';
import flyShieldApiService from '../services/flyShieldApiService';
import flyshieldCatalogService from '../services/flyshieldCatalogService';
import { useAuth } from '../context/AuthContext';

const FlyShieldForm = () => {
  const { user } = useAuth();

  // Campos preservados (email preenchido automaticamente + data/hora da coleta)
  const [formData, setFormData] = useState({
    userEmail: '',
    collectDateTime: new Date().toISOString().slice(0, 19) + 'Z'
  });

  // Árvore de autorização do usuário
  const [auth, setAuth] = useState(null);
  const [userId, setUserId] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Combos encadeados (estado "não selecionado" é sempre string vazia '')
  const [companyId, setCompanyId] = useState('');
  const [companyUnitId, setCompanyUnitId] = useState('');
  const [companyFarmId, setCompanyFarmId] = useState('');
  const [trickSectionFieldId, setTrickSectionFieldId] = useState('');
  const [flyTrapId, setFlyTrapId] = useState('');

  // Armadilhas do talhão selecionado
  const [traps, setTraps] = useState([]);
  const [trapsLoading, setTrapsLoading] = useState(false);
  const [trapsError, setTrapsError] = useState(null);

  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const [requestPayload, setRequestPayload] = useState(null);
  const [fullRequestPayload, setFullRequestPayload] = useState(null);

  // Preenche o userEmail automaticamente com o email do usuário autenticado via OIDC
  useEffect(() => {
    if (user?.profile?.email) {
      setFormData(prev => ({ ...prev, userEmail: user.profile.email }));
    }
  }, [user]);

  // Carrega a árvore de autorização do usuário no mount / quando o usuário muda
  useEffect(() => {
    const email = user?.profile?.email;
    if (!email) return;

    let cancelled = false;
    setAuthLoading(true);
    setAuthError(null);

    flyshieldCatalogService
      .getUserAuthorization(email)
      .then((data) => {
        if (cancelled) return;
        setAuth(data);
        setUserId(typeof data?.userId === 'number' ? data.userId : null);
      })
      .catch((err) => {
        if (cancelled) return;
        setAuthError(err.message || 'Falha ao carregar a autorização do usuário.');
      })
      .finally(() => {
        if (!cancelled) setAuthLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Carrega as armadilhas quando há talhão selecionado; limpa quando não há
  useEffect(() => {
    if (trickSectionFieldId === '') {
      setTraps([]);
      setTrapsError(null);
      return;
    }

    let cancelled = false;
    setTrapsLoading(true);
    setTrapsError(null);

    flyshieldCatalogService
      .getFlyTrapsBySection({
        unit: Number(companyUnitId),
        farm: Number(companyFarmId),
        section: Number(trickSectionFieldId)
      })
      .then((data) => {
        if (cancelled) return;
        setTraps(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setTrapsError(err.message || 'Falha ao carregar as armadilhas.');
        setTraps([]);
      })
      .finally(() => {
        if (!cancelled) setTrapsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyUnitId, companyFarmId, trickSectionFieldId]);

  // Opções derivadas da árvore de autorização (filtradas no cliente com Number(...))
  const authorizedCompanies = auth?.authorizedCompanies ?? [];
  const empresaOptions = authorizedCompanies.filter(c => c.isAuthorized !== false);
  const empresaSel = authorizedCompanies.find(c => c.companyId === Number(companyId));
  const unidadeOptions = empresaSel?.companyUnit ?? [];
  const fazendaOptions = (empresaSel?.companyFarm ?? []).filter(
    f => f.companyUnitId === Number(companyUnitId)
  );
  const talhaoOptions = (empresaSel?.trickSectionField ?? []).filter(
    s => s.companyFarmId === Number(companyFarmId)
  );

  const clearFieldError = (name) => {
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    clearFieldError(name);
  };

  // Handlers dos combos com reset em cascata dos descendentes para ''
  const handleCompanyChange = (e) => {
    setCompanyId(e.target.value);
    setCompanyUnitId('');
    setCompanyFarmId('');
    setTrickSectionFieldId('');
    setFlyTrapId('');
    clearFieldError('companyId');
  };

  const handleUnitChange = (e) => {
    setCompanyUnitId(e.target.value);
    setCompanyFarmId('');
    setTrickSectionFieldId('');
    setFlyTrapId('');
    clearFieldError('companyUnitId');
  };

  const handleFarmChange = (e) => {
    setCompanyFarmId(e.target.value);
    setTrickSectionFieldId('');
    setFlyTrapId('');
    clearFieldError('companyFarmId');
  };

  const handleSectionChange = (e) => {
    setTrickSectionFieldId(e.target.value);
    setFlyTrapId('');
    clearFieldError('trickSectionFieldId');
  };

  const handleTrapChange = (e) => {
    setFlyTrapId(e.target.value);
    clearFieldError('flyTrapId');
  };

  const handleImagesChange = (base64Images) => {
    setImages(base64Images);
    if (validationErrors.images) {
      setValidationErrors(prev => ({
        ...prev,
        images: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (companyId === '') {
      errors.companyId = 'Selecione a empresa';
    }
    if (companyUnitId === '') {
      errors.companyUnitId = 'Selecione a unidade';
    }
    if (companyFarmId === '') {
      errors.companyFarmId = 'Selecione a fazenda';
    }
    if (trickSectionFieldId === '') {
      errors.trickSectionFieldId = 'Selecione o talhão';
    }
    if (flyTrapId === '') {
      errors.flyTrapId = 'Selecione a armadilha';
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.userEmail || !emailRegex.test(formData.userEmail)) {
      errors.userEmail = 'Valid email address is required';
    }

    // Validate date
    if (!formData.collectDateTime) {
      errors.collectDateTime = 'Collection date and time is required';
    }

    // Validate images
    if (images.length !== 3) {
      errors.images = 'Exactly 3 photos are required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous response and error
    setResponse(null);
    setError(null);

    if (authLoading) {
      return;
    }
    if (authError) {
      setError('Não é possível enviar: falha ao carregar a autorização do usuário.');
      return;
    }

    // Validate form
    if (!validateForm()) {
      return;
    }

    const armadilhaSelecionada = traps.find(t => t.id === Number(flyTrapId));
    if (!armadilhaSelecionada) {
      setError('Armadilha selecionada inválida. Selecione novamente.');
      return;
    }

    if (typeof userId !== 'number' || userId <= 0) {
      setError('Não foi possível obter o ID numérico do usuário. Verifique seu cadastro na API.');
      return;
    }

    setIsLoading(true);

    try {
      // Prepare request data
      const requestData = {
        photo01: images[0] || '',
        photo02: images[1] || '',
        photo03: images[2] || '',
        photoProcessed01: '',
        photoProcessed02: '',
        photoProcessed03: '',
        companyId: Number(companyId),
        companyUnitId: Number(companyUnitId),
        companyFarmId: Number(companyFarmId),
        trickSectionFieldId: Number(trickSectionFieldId),
        flyTrapId: Number(flyTrapId),
        latitude: String(armadilhaSelecionada.latitude),
        longitude: String(armadilhaSelecionada.longitude),
        userEmail: formData.userEmail,
        collectDateTime: formData.collectDateTime,
        userId: userId
      };

      // Store the payload for display (with truncated images)
      const displayPayload = {
        ...requestData,
        photo01: requestData.photo01 ? `${requestData.photo01.substring(0, 100)}... [${requestData.photo01.length} chars]` : '',
        photo02: requestData.photo02 ? `${requestData.photo02.substring(0, 100)}... [${requestData.photo02.length} chars]` : '',
        photo03: requestData.photo03 ? `${requestData.photo03.substring(0, 100)}... [${requestData.photo03.length} chars]` : ''
      };
      setRequestPayload(displayPayload);
      setFullRequestPayload(requestData);

      // Make API request
      const result = await flyShieldApiService.submitDetectorRequest(requestData);

      if (result.success) {
        setResponse(result);
        setError(null);
      } else {
        setError(result.error);
        setResponse(result);
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
      setResponse(null);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setCompanyId('');
    setCompanyUnitId('');
    setCompanyFarmId('');
    setTrickSectionFieldId('');
    setFlyTrapId('');
    setTraps([]);
    setTrapsError(null);
    setFormData({
      userEmail: user?.profile?.email || '',
      collectDateTime: new Date().toISOString().slice(0, 19) + 'Z'
    });
    setImages([]);
    setResponse(null);
    setError(null);
    setValidationErrors({});
    setRequestPayload(null);
    setFullRequestPayload(null);
  };

  const getInputClassName = (fieldName) => {
    return `form-control ${validationErrors[fieldName] ? 'is-invalid' : ''}`;
  };

  const getSelectClassName = (fieldName) => {
    return `form-select ${validationErrors[fieldName] ? 'is-invalid' : ''}`;
  };

  const downloadPayload = () => {
    if (!fullRequestPayload) return;

    const dataStr = JSON.stringify(fullRequestPayload, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `flyshield-request-${new Date().getTime()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const combosDisabled = authLoading || !!authError;

  return (
    <div className="container-fluid">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="form-section">
            <div className="text-center mb-4">
              <h2 className="text-primary">FlyShield API Tester</h2>
              <p className="text-muted">
                Test the FlyShield indirect monitoring detector request endpoint
              </p>
            </div>

            {authError && (
              <div className="alert alert-danger" role="alert">
                {authError}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Image Upload Section */}
              <div className="mb-4">
                <ImageUpload
                  onImagesChange={handleImagesChange}
                  maxImages={3}
                />
                {validationErrors.images && (
                  <div className="text-danger small mt-1">{validationErrors.images}</div>
                )}
              </div>

              {/* Company Information */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="companyId" className="form-label">Empresa</label>
                  <select
                    className={getSelectClassName('companyId')}
                    id="companyId"
                    name="companyId"
                    value={companyId}
                    onChange={handleCompanyChange}
                    disabled={combosDisabled}
                    required
                  >
                    <option value="">Selecione…</option>
                    {empresaOptions.map(c => (
                      <option key={c.companyId} value={c.companyId}>{c.companyName}</option>
                    ))}
                  </select>
                  {validationErrors.companyId && (
                    <div className="invalid-feedback">{validationErrors.companyId}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label htmlFor="companyUnitId" className="form-label">Unidade</label>
                  <select
                    className={getSelectClassName('companyUnitId')}
                    id="companyUnitId"
                    name="companyUnitId"
                    value={companyUnitId}
                    onChange={handleUnitChange}
                    disabled={combosDisabled || companyId === ''}
                    required
                  >
                    <option value="">Selecione…</option>
                    {unidadeOptions.map(u => (
                      <option key={u.id} value={u.id}>{u.description}</option>
                    ))}
                  </select>
                  {validationErrors.companyUnitId && (
                    <div className="invalid-feedback">{validationErrors.companyUnitId}</div>
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="companyFarmId" className="form-label">Fazenda</label>
                  <select
                    className={getSelectClassName('companyFarmId')}
                    id="companyFarmId"
                    name="companyFarmId"
                    value={companyFarmId}
                    onChange={handleFarmChange}
                    disabled={combosDisabled || companyUnitId === ''}
                    required
                  >
                    <option value="">Selecione…</option>
                    {fazendaOptions.map(f => (
                      <option key={f.id} value={f.id}>{f.description}</option>
                    ))}
                  </select>
                  {validationErrors.companyFarmId && (
                    <div className="invalid-feedback">{validationErrors.companyFarmId}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label htmlFor="trickSectionFieldId" className="form-label">Talhão</label>
                  <select
                    className={getSelectClassName('trickSectionFieldId')}
                    id="trickSectionFieldId"
                    name="trickSectionFieldId"
                    value={trickSectionFieldId}
                    onChange={handleSectionChange}
                    disabled={combosDisabled || companyFarmId === ''}
                    required
                  >
                    <option value="">Selecione…</option>
                    {talhaoOptions.map(s => (
                      <option key={s.id} value={s.id}>{s.description}</option>
                    ))}
                  </select>
                  {validationErrors.trickSectionFieldId && (
                    <div className="invalid-feedback">{validationErrors.trickSectionFieldId}</div>
                  )}
                </div>
              </div>

              {/* Trap Information */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="flyTrapId" className="form-label">Armadilha</label>
                  <select
                    className={getSelectClassName('flyTrapId')}
                    id="flyTrapId"
                    name="flyTrapId"
                    value={flyTrapId}
                    onChange={handleTrapChange}
                    disabled={combosDisabled || trickSectionFieldId === '' || trapsLoading}
                    required
                  >
                    <option value="">Selecione…</option>
                    {traps.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.subDescription ? `${t.description} - ${t.subDescription}` : t.description}
                      </option>
                    ))}
                  </select>
                  {trapsLoading && (
                    <div className="form-text">Carregando armadilhas…</div>
                  )}
                  {trapsError && (
                    <div className="text-danger small mt-1">{trapsError}</div>
                  )}
                  {validationErrors.flyTrapId && (
                    <div className="invalid-feedback">{validationErrors.flyTrapId}</div>
                  )}
                </div>
              </div>

              {/* User Information */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="userEmail" className="form-label">User Email</label>
                  <input
                    type="email"
                    className={getInputClassName('userEmail')}
                    id="userEmail"
                    name="userEmail"
                    value={formData.userEmail}
                    onChange={handleInputChange}
                    readOnly
                    required
                  />
                  <div className="form-text">Preenchido automaticamente com o email do usuário autenticado.</div>
                  {validationErrors.userEmail && (
                    <div className="invalid-feedback">{validationErrors.userEmail}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label htmlFor="collectDateTime" className="form-label">Data e Hora da coleta da imagem</label>
                  <input
                    type="datetime-local"
                    className={getInputClassName('collectDateTime')}
                    id="collectDateTime"
                    name="collectDateTime"
                    value={formData.collectDateTime.slice(0, 19)}
                    onChange={(e) => handleInputChange({
                      target: {
                        name: 'collectDateTime',
                        value: e.target.value + 'Z',
                        type: 'text'
                      }
                    })}
                    required
                  />
                  {validationErrors.collectDateTime && (
                    <div className="invalid-feedback">{validationErrors.collectDateTime}</div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex gap-3 justify-content-between">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={resetForm}
                  disabled={isLoading}
                >
                  Reset Form
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    isLoading ||
                    images.length !== 3 ||
                    combosDisabled ||
                    companyId === '' ||
                    companyUnitId === '' ||
                    companyFarmId === '' ||
                    trickSectionFieldId === '' ||
                    flyTrapId === ''
                  }
                >
                  {isLoading ? (
                    <>
                      <span className="loading-spinner me-2"></span>
                      Sending Request...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Request Payload Display */}
          {requestPayload && (
            <div className="response-container mt-3" style={{ borderLeft: '4px solid #007bff' }}>
              <div className="d-flex align-items-start">
                <i className="bi bi-code-square text-primary fs-4 me-3 mt-1"></i>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="text-primary mb-0">Request Payload Sent</h5>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={downloadPayload}
                      title="Download full payload with complete base64 images"
                    >
                      <i className="bi bi-download me-1"></i>
                      Download Full Payload
                    </button>
                  </div>
                  <p className="text-muted mb-2">
                    <small>
                      <strong>Endpoint:</strong> POST {' '}
                      {/* <code>https://api-flyshield-stg.azurewebsites.net/api/indirectmonitoring/flyshield-detector-request</code> */}
                      {/* <code>https://api-flyshield-dev.azurewebsites.net/api/indirectmonitoring/flyshield-detector-request</code> */}
                      <code>https://flyshield-api.agroverita.com.br/api/indirectmonitoring/flyshield-detector-request</code>
                    </small>
                  </p>
                  <div className="json-display">
                    {JSON.stringify(requestPayload, null, 2)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Response Display */}
          <ResponseDisplay
            response={response}
            isLoading={isLoading}
            error={error}
          />
        </div>
      </div>
    </div>
  );
};

export default FlyShieldForm;
